import { secondsPerHour, secondsPerMinute } from "./time";

/**
 * Returns the whole minutes shown for a duration: rounded down to the whole minute below, also for
 * negative balances (−55:59:59 is shown as −56:00), like `DurationDisplay` in the backend (F-61,
 * ADR-0189, `shared-test-cases/duration-display.json`). With targets in whole minutes the shown
 * balance then equals the shown target minus the shown actual time.
 *
 * @param totalSeconds - Duration in seconds.
 * @returns The minutes, rounded down.
 */
export function wholeMinutes(totalSeconds: number): number {
  const minutes = Math.floor(totalSeconds / secondsPerMinute);
  return minutes;
}

/**
 * Formats a duration as hours and minutes (`7:05`). Seconds are dropped only for display, by
 * rounding down to the whole minute ({@link wholeMinutes}); stored values keep whole seconds
 * (spec 6.1.4). Negative values get a minus sign.
 *
 * @param totalSeconds - Duration in seconds.
 * @returns The formatted duration without unit.
 */
export function formatHoursMinutes(totalSeconds: number): string {
  const shown = wholeMinutes(totalSeconds);
  const sign = shown < 0 ? "−" : "";
  const absolute = Math.abs(shown);
  const minutesPerHour = secondsPerHour / secondsPerMinute;
  const hours = Math.floor(absolute / minutesPerHour);
  const minutes = absolute % minutesPerHour;
  const formatted = `${sign}${String(hours)}:${String(minutes).padStart(2, "0")}`;

  return formatted;
}

/**
 * Formats a running duration with seconds (`7:05:09`) for live displays.
 *
 * @param totalSeconds - Duration in seconds (negative values are shown as 0).
 * @returns The formatted duration.
 */
export function formatClock(totalSeconds: number): string {
  const absolute = Math.max(0, Math.trunc(totalSeconds));
  const hours = Math.floor(absolute / secondsPerHour);
  const minutes = Math.floor((absolute % secondsPerHour) / secondsPerMinute);
  const seconds = absolute % secondsPerMinute;
  const formatted = `${String(hours)}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

  return formatted;
}

/**
 * Sums durations.
 *
 * @param durations - Durations in seconds.
 * @returns The sum.
 */
export function sumSeconds(durations: readonly number[]): number {
  const total = durations.reduce((accumulator, value) => accumulator + value, 0);

  return total;
}
